import * as Sentry from '@sentry/react';
import { getApiEndpoints, healthUriFor } from '../config/apiEndpoints';

const STORAGE_KEY = 'rois_api_endpoint_v1';
const FAILURES_TO_SWITCH = 2;
const PROBE_TIMEOUT_MS = 2500;
const PROBE_BACK_INTERVAL_MS = 5 * 60 * 1000;
/** Códigos HTTP que significan "el endpoint está caído" (gateway/proxy muerto),
 *  no "la app respondió mal". 4xx/500 de aplicación NUNCA conmutan. */
const GATEWAY_DOWN_CODES = new Set([502, 503, 504]);

async function probe(healthUri: string): Promise<boolean> {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(healthUri, { signal: controller.signal, cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/** ¿El error señala endpoint caído? (red pura o gateway 502/503/504)
 *  Lo comparte el cortacircuitos (`offline/serverDownBreaker.ts`): cuenta lo
 *  mismo que el failover, ni más ni menos. */
export function isEndpointDownError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { statusCode?: number; name?: string; message?: string };
  // ServerError/ServerParseError con respuesta HTTP: solo gateway muerto.
  if (typeof e.statusCode === 'number') return GATEWAY_DOWN_CODES.has(e.statusCode);
  // Sin statusCode: SOLO errores de transporte genuinos (allowlist, fail-closed).
  // Un error sintético de un link intermedio (p. ej. GraphQLError de refresh
  // caducado que sube por observer.error) NO debe contar como endpoint caído.
  if (e.name === 'TypeError' || e.name === 'AbortError') return true;
  return (
    typeof e.message === 'string' &&
    /failed to fetch|networkerror|network request failed|load failed/i.test(e.message)
  );
}

class ApiEndpointManager {
  private endpoints: string[];
  private index = 0;
  private consecutiveFailures = 0;
  private switching = false;
  private probeBackTimer: ReturnType<typeof setInterval> | null = null;

  constructor(endpoints: string[]) {
    this.endpoints = endpoints;
    const saved = localStorage.getItem(STORAGE_KEY);
    const savedIndex = saved ? endpoints.indexOf(saved) : -1;
    if (savedIndex > 0) {
      this.index = savedIndex;
      this.startProbeBack(); // arrancamos en respaldo → vigilar la primaria
      // H4: no esperar al primer tick del interval (5 min) — sondea la primaria ya.
      void this.tryReturnToPrimary();
    }
    this.registerReturnTriggers();
  }

  get hasBackup(): boolean {
    return this.endpoints.length > 1;
  }

  currentUri(): string {
    // Defensa en profundidad: la forma función de `uri` en Apollo pierde el
    // default '/graphql' que tenía la forma string si endpoints quedara vacío.
    return this.endpoints[this.index] ?? '/graphql';
  }

  reportSuccess(): void {
    this.consecutiveFailures = 0;
  }

  reportFailure(error: unknown): void {
    if (!this.hasBackup || this.switching) return;
    if (!navigator.onLine) return; // offline del usuario: no es cosa nuestra
    if (!isEndpointDownError(error)) return;
    this.consecutiveFailures += 1;
    if (this.consecutiveFailures >= FAILURES_TO_SWITCH) {
      void this.trySwitch();
    }
  }

  /** Conmuta al siguiente endpoint SOLO si su /health responde (anti-flapping). */
  private async trySwitch(): Promise<void> {
    this.switching = true;
    try {
      const nextIndex = (this.index + 1) % this.endpoints.length;
      if (nextIndex === this.index) return;
      const ok = await probe(healthUriFor(this.endpoints[nextIndex]));
      if (!ok) return; // destino también caído → offline real o blip; no conmutar
      this.index = nextIndex;
      this.consecutiveFailures = 0;
      localStorage.setItem(STORAGE_KEY, this.endpoints[this.index]);
      Sentry.setTag('api_endpoint', this.index === 0 ? 'primary' : 'backup');
      if (this.index !== 0) {
        // Evento explícito (no solo tag): agrupa en UN issue de Sentry por
        // incidente → regla de alerta = aviso al equipo de que la primaria
        // cayó y los clientes están conmutando.
        Sentry.captureMessage(
          '[failover] API primaria caída — conmutado al respaldo',
          'warning',
        );
        this.startProbeBack();
      } else {
        this.stopProbeBack();
      }
    } finally {
      this.switching = false;
    }
  }

  /** En respaldo: cada 5 min probamos la primaria; si vuelve, retorno en silencio. */
  private startProbeBack(): void {
    if (this.probeBackTimer) return;
    this.probeBackTimer = setInterval(() => {
      void this.tryReturnToPrimary();
    }, PROBE_BACK_INTERVAL_MS);
  }

  /** Sondea la primaria y, si responde, retorna a ella en silencio (sticky +
   *  Sentry tag + detener el probe). Guard `switching` compartido con `trySwitch`
   *  para no cruzarse con una conmutación en curso. */
  private async tryReturnToPrimary(): Promise<void> {
    if (this.switching) return;
    this.switching = true;
    try {
      if (await probe(healthUriFor(this.endpoints[0]))) {
        this.index = 0;
        this.consecutiveFailures = 0;
        localStorage.setItem(STORAGE_KEY, this.endpoints[0]);
        Sentry.setTag('api_endpoint', 'primary');
        Sentry.captureMessage(
          '[failover] primaria recuperada — retorno automático',
          'info',
        );
        this.stopProbeBack();
      }
    } finally {
      this.switching = false;
    }
  }

  /** Además del interval, disparamos un retorno inmediato cuando el entorno
   *  cambia a favor (recuperación de red o la pestaña vuelve a primer plano),
   *  para acortar la ventana de retorno de ≤5 min a segundos. Registro único
   *  (singleton de vida completa; no se desregistra). Protegido contra
   *  contextos sin DOM (`window`/`document` ausentes). */
  private registerReturnTriggers(): void {
    const trigger = (): void => {
      if (!this.hasBackup || this.index === 0 || !navigator.onLine) return;
      void this.tryReturnToPrimary();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', trigger);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') trigger();
      });
    }
  }

  private stopProbeBack(): void {
    if (this.probeBackTimer) {
      clearInterval(this.probeBackTimer);
      this.probeBackTimer = null;
    }
  }

  /** ¿Hay ALGÚN backend alcanzable? (para checkConnectivity/colas offline).
   *  Probes en PARALELO: el peor caso es UN timeout (2,5 s), no la suma —
   *  con la primaria caída, esperar en serie duplicaba la latencia de los
   *  gates de guardado (NewAction/AddVehicle).
   *
   *  Y basta con el PRIMERO que conteste bien (28/09): con `Promise.all` se
   *  esperaba a todos, así que un respaldo caído costaba su plazo entero en
   *  cada guardado aunque la primaria respondiera en 50 ms (medido en tablet:
   *  «Continuar» de Nueva actuación, 2,3 s de espera). «No hay backend» sí
   *  necesita que fallen todos. `probe` nunca rechaza. */
  anyReachable(): Promise<boolean> {
    if (!navigator.onLine) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      let pending = this.endpoints.length;
      if (pending === 0) {
        resolve(false);
        return;
      }
      for (const endpoint of this.endpoints) {
        void probe(healthUriFor(endpoint)).then((ok) => {
          if (ok) resolve(true);
          else if (--pending === 0) resolve(false);
        });
      }
    });
  }

  _resetForTests(): void {
    this.index = 0;
    this.consecutiveFailures = 0;
    this.switching = false;
    this.stopProbeBack();
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const apiEndpointManager = new ApiEndpointManager(getApiEndpoints());
